import { configureStore } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { setAutoFreeze, enableES5 } from 'immer';
import captureSpecificError from '../capture-error';
import {
  reducer as appControls,
  selectLocationConfig,
  updateLocationSharingSettings,
} from './modules/app-controls';
import { reducer as planDetails } from './modules/plan';
import { reducer as backendService } from './modules/backend-service';
import { reducer as courses } from './modules/courses';
import floatPlan from './modules/float-plan';
import { reducer as follows } from './modules/follows';
import { reducer as geoService } from './modules/geo-service';
import { reducer as incidentReport } from './modules/incident-report';
import logTrips, { loadActiveTrip } from './modules/log-trips';
import { reducer as marketplace } from './modules/marketplace';
import { reducer as notifications } from './modules/notifications';
import { reducer as mapSettings } from './modules/map-settings';
import { reducer as subscriptions } from './modules/subscriptions';
import { reducer as partners } from './modules/partners';
import sharing from './modules/sharing';
import {
  fetchLoggedInMembersPositions,
  getOnTheWaterFromState,
  reducer as signal,
} from './modules/signal';
import { reducer as temporaryMarkers } from './modules/temporaryMarkers';
import { reducer as vessels } from './modules/vessels';
import { reducer as prepare } from './modules/prepare';
import { reducer as weather } from './modules/weather';
import {
  backgroundGeolocationStart,
  checkLocationPermission,
  isBackgroundGeolocationDisclosed,
} from '../log-trips/background-geolocation';
import { transitionToNextRoute } from '../route/route-navigation';
import { ROUTE_MAP, ROUTE_ON_THE_WATER_PROMPT } from '../route/route-names';

// NOTE These imports HAVE to be last. I haven't worked out why. FML
import { listenerMiddleware } from './listenerMiddleware';
import { middleware as identityMiddleware } from './memberIdentifyMiddleware';
import { middleware as autoFollowMiddleware } from './autoFollowMiddleware';
import { hasFeatureEnabled } from '../feature';
import { captureEvent } from '../event-capture';
import isMapVisible from '../../components/helpers/is-map-visible';

const reloadMembersPositionInterval =
  window?.DECKEE_CONFIG?.reloadMembersPositionInterval || 10000;
// Fallback to 10 seconds in case of null which resolves to 0 and is bad

// Configure Immer to avoid eval() usage for CSP compliance
setAutoFreeze(false);
enableES5();

/**
 * NOTES ON TYPES
 *
 * Because our store is wrapped in an "initialize", we need to store a reference to what is created, so when we call "getStore" it will return the same instance.
 * This has messed with types a little (and could be refactored). The `currentStore` is typed as the `ReturnType` of the initialize function to infer the appropriate types.
 */

/** Pass `enableSideEffects: false` to skip the background listeners and pollers. Used in Storybook or tests. */
export const initialize = ({ initialState = {}, enableSideEffects = true }) => {
  const store = configureStore({
    reducer: {
      // TODO: Fix this ts-ignore
      // @ts-expect-error I don't know why this error was occurring
      appControls,
      logTrips,
      backendService,
      geoService,
      weather,
      sharing,
      floatPlan,
      planDetails,
      signal,
      follows,
      vessels,
      prepare,
      incidentReport,
      marketplace,
      courses,
      temporaryMarkers,
      notifications,
      mapSettings,
      subscriptions,
      partners,

      // Fix: "React-Redux: Combining reducers: Unexpected Keys"
      // http://stackoverflow.com/a/33678198/789076
      // TODO: check this, do we even need to add the configuration to the store anymore?
      // The values in this configuration object are static for the session of the app so it might
      // make more sense to not use the store for these values.
      configurations: (state = {}) => state,
    },
    // @ts-expect-error: Not sure how the types work with redux middlware
    middleware: (getDefaultMiddleware) => {
      if (enableSideEffects) {
        autoFollowMiddleware();
        identityMiddleware();
      }
      return getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false, // Disable Immer's deep equality checks that use eval
        thunk: {
          extraArgument: undefined,
        },
      }).prepend(listenerMiddleware.middleware);
    },

    preloadedState: initialState,
  });

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  currentStore = store;

  if (!enableSideEffects) {
    return store;
  }

  let processingOtwChange = false;
  let previousOtwState: boolean | undefined;
  store.subscribe(async () => {
    // Subscribe can get spammy, so make sure we are only processing one change at a time
    if (processingOtwChange) {
      return;
    }

    processingOtwChange = true;
    const state = store.getState();

    // Get if the user is
    const userIsOTW = getOnTheWaterFromState(state);
    const { onTheWaterDetectionEnabled } = selectLocationConfig(state);
    if (
      userIsOTW === previousOtwState ||
      userIsOTW === null ||
      userIsOTW === undefined
    ) {
      processingOtwChange = false;
      return;
    }

    try {
      if (
        hasFeatureEnabled('on-the-water-detection') &&
        Capacitor.isNativePlatform()
      ) {
        const permissionGranted = await checkLocationPermission();
        const disclosureState = await isBackgroundGeolocationDisclosed();
        if (!disclosureState) {
          // Scenario: New users signing up to DECKEE
          // Given a person who is signing up to DECKEE
          // When they completed onboarding
          // Then They will be prompted with the thing

          // Scenario: Existing user whos settings are not correct
          // Given an existing user
          // And they DON'T have location set to always "always"
          // When they open the app
          // Then They will be prompted with the thing

          // Scenario: Existing user whos settings are correct
          // Given an existing user
          // And they have already enabled their location setting to "always"
          // When they open the app
          // And they are signed in
          // Then They will be prompted with the thing

          transitionToNextRoute(ROUTE_ON_THE_WATER_PROMPT, {
            nextRoute: ROUTE_MAP,
          });
        } else if (
          onTheWaterDetectionEnabled &&
          disclosureState &&
          permissionGranted
        ) {
          captureEvent({
            type: 'state: attempt starting background geolocation',
            payload: {},
          });
          await backgroundGeolocationStart({ lowFrequencyTracking: true });
        } else if (
          onTheWaterDetectionEnabled &&
          disclosureState &&
          !permissionGranted
        ) {
          // Scenario: Not setting native prompt to always
          // Given an existing user
          // When they set their background location setting NOT to "always"
          // Then OTW will be turned off for that user
          store.dispatch(
            updateLocationSharingSettings({
              onTheWaterDetectionEnabled: false,
            }),
          );
        }
      }
    } catch (e: any) {
      captureSpecificError(
        e,
        // Current file path
        'src/store/index.ts',
      );
    }
    previousOtwState = userIsOTW;
    processingOtwChange = false;

    // User is on the water, and it's different from their previous state, we should attempt to load an active trip.
    // Note, there is a race condition here where the user could be on the water, but the active trip hasn't been loaded yet.
    store.dispatch(loadActiveTrip());
  });

  // Continually reload the users positions
  setInterval(async () => {
    const { isActive } = await App.getState();
    if (
      isActive &&
      isMapVisible() &&
      store.getState().backendService?.currentMember
    ) {
      store.dispatch(fetchLoggedInMembersPositions());
    }
  }, reloadMembersPositionInterval);
  return store;
};

let currentStore: ReturnType<typeof initialize>;

export const getStore = () => currentStore;

export type AppState = ReturnType<typeof currentStore.getState>;
export type AppGetStore = () => AppState;
export type AppDispatch = typeof currentStore.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;

// This is to be used in the createAsyncThunk - I haven't seen how this can be inferred as of yet...
export type AppAsyncThunkAPI = {
  state: AppState;
};
